import { EQuizMode } from '@constants';
import { Button } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Wrapper, InstructionsWrapper } from './ModalFooter.styles';

import AI_UCADEMY_ICON from '@/assets/ai_ucademy_icon_white.svg';
import { Icon } from '@/components/Icon';
import { useQuizForm } from '@/hooks';
import { useProfile } from '@/store';
import { buildQuizUrl } from '@/utils';

type Props = {
  step: number;
  handleNextSlide: () => void;
  handleBackSlide: () => void;
  handleGenerateQuiz: () => void;
  isLoading: boolean;
  isFromBaseContent?: boolean;
};

export const ModalFooter: React.FC<Props> = ({
  step,
  handleNextSlide,
  handleBackSlide,
  handleGenerateQuiz,
  isLoading,
  isFromBaseContent
}) => {
  const { t } = useTranslation('ManagerQuizzesTranslations');
  const { config, isInstructionsOpen, setIsInstructionsOpen, questions, setConfigOpen } =
    useQuizForm();
  const {
    roleCheck: { isTeacher }
  } = useProfile();
  const navigateTo = useNavigate();

  const handleNavigateToQuiz = () => {
    setConfigOpen(false);
    navigateTo(buildQuizUrl({ isFromBaseContent, isTeacher }));
  };

  const canSave = useMemo(() => {
    if (config.mode === EQuizMode.QUIZ || config.mode === EQuizMode.TRUE_FALSE)
      return !!config.title && !!config.course && !!config.section;

    return !!config.title && !!config.course && !!config.timer;
  }, [config.course, config.mode, config.section, config.timer, config.title]);

  if (step === 0) return null;

  if (step === 1)
    return (
      <Button type="primary" loading={isLoading} disabled={!canSave} onClick={handleNextSlide}>
        {t('CREATE_QUIZ')}
      </Button>
    );

  if (step === 2 && !config.generationType) return null;

  if (step === 2 && config.generationType === 'AI')
    return (
      <Wrapper>
        <Button
          type="text"
          onClick={handleBackSlide}
          loading={isLoading}
          icon={<Icon i="ArrowLeft" />}>
          {t('BACK')}
        </Button>
        <Button
          type="ai-primary"
          loading={isLoading}
          disabled={!config.questionCount || !config.selectedFiles?.length}
          icon={<img src={AI_UCADEMY_ICON} alt="AI Ucademy" />}
          onClick={handleGenerateQuiz}>
          {t('AI_QUESTION_GENERATION')}
        </Button>
      </Wrapper>
    );

  if (step === 2 && config.generationType === 'IMPORT') {
    if (isInstructionsOpen)
      return (
        <InstructionsWrapper>
          <Button onClick={() => setIsInstructionsOpen(false)}>{t('ACKNOWLEDGED')}</Button>
        </InstructionsWrapper>
      );
    return (
      <Wrapper>
        <Button
          type="text"
          onClick={handleBackSlide}
          loading={isLoading}
          icon={<Icon i="ArrowLeft" />}>
          {t('BACK')}
        </Button>
        <Button
          type="primary"
          loading={isLoading}
          disabled={!questions?.length}
          onClick={handleNavigateToQuiz}>
          {t('IMPORT_QUESTIONS')}
        </Button>
      </Wrapper>
    );
  }

  return null;
};
